'use client';

import { ReactNode, useEffect, useMemo } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';

import { redirect, usePathname } from 'next/navigation';

export default abstract class AuthGuardService {
   static isAuth = (component: ReactNode, redirectUser: boolean, language?: string, allowedGroups?: UserGroups[]): ReactNode | null => {
      const { loading, user, login } = useAuthentication();
      const pathName = usePathname();

      const userHasAccess = useMemo(() => {
         /**
          * Temporary check on redirectUser until we refactored the AccessLevel field in Contentful (Entry level)
          * Contentful still uses radio buttons so there's always one selected option.
          * When this option is the default eg: public, we need to show the page regardless of the user.
          *
          * !redirectUser in this context means we're dealing with a page-builder element.
          *
          * When dealing with page-builder elements, we check if the user is logged in. If public is the only allowed group,
          * we do not show the element this user.
          */

         // Only public access is allowed.
         if (allowedGroups?.length === 1 && allowedGroups.includes(UserGroups.public) && !redirectUser) return !user;

         return user?.groups.some((group) => allowedGroups?.includes(group)) || allowedGroups?.includes(UserGroups.public);
      }, [allowedGroups, user]);

      useEffect(() => {
         if (!loading && !userHasAccess && redirectUser) {
            if (user) {
               redirect(`/`);
            } else {
               login(pathName);
            }
         }
      }, [loading, userHasAccess, pathName, redirectUser, language]);

      if (!userHasAccess) {
         if (loading && redirectUser) {
            return (
               <Box spacing="lg" width="100%" direction="column" items="center">
                  <Spinner />
               </Box>
            );
         }

         return null;
      }

      return component;
   };
}
